import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import DateComponent from 'desktop/components/Date';
import Loader from 'desktop/components/Loader';
import Program from 'desktop/components/Program';
import { addZero } from 'desktop/utils/addZero';
import {
  StyledText,
  DailyContent,
  DailyProgramStyled,
  NoPrograms,
  NoProgramsImage,
  DaysContainer,
  StyledScrollbar,
  AbsoluteWrapperStyled,
} from './styles/DailyProgramStyled';
import DateHeaderCap from './DateHeaderCap';

const MONTHS_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const listStyle = {
  overflowX: false,
  overflowY: false,
};

class DailyProgram extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      programs: [],
      days: [],
      loading: props.currentChannelId !== null, // if currentChannelId is null this indicates, that current channel does not present in epg's channels list
      scrollbarScrollTo: { value: -1 },
    };
    this.smallScreen = window.innerWidth <= 1120;
    this.isProgramExtended = false;
    this.scrollTarget = {};
    this.topOffset = this.smallScreen ? 0 : 30;
    this.dayTopOffset = this.smallScreen ? 0 : 85;
    this.lastProgramOffset = this.smallScreen ? 0 : 15;
    this.listRef = null;
    this.resizeTimeoutId = null;
    this.cache = new CellMeasurerCache({
      minHeight: 50,
      fixedWidth: true,
    });
    this.throttledOnResize = _.throttle(this.onResize, 500);
    this.scrollToIndex = -1;
    this.needExecScrollToRow = false;
    this.programComponentList = {};
    this.firstDateCapInstance = null;
    this.root = React.createRef();
    this.targetScrollReseted = false;
    this.scrollToRowSet = false;

    this.positionMap = {};
    this.dateTranslateYMap = {};
  }

  componentDidMount() {
    const { programsList, programsMap, isExtendedProgramList } = this.props;
    window.addEventListener('resize', this.throttledOnResize);

    if (programsMap && programsList && isExtendedProgramList) {
      this.processPrograms(this.props.programsList, this.props.programsMap);
    }

    this.fillKeyElementsMap();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      programsList: nextProgramsList,
      loadPrograms,
      programsMap,
      currentProgramTimestamp: nextCurrentProgramTimestamp,
      playingProgramTimestamp: nextPlayingProgramTimestamp,
      visible: nextVisible,
      currentChannelId: nextChannelId,
      isExtendedProgramList,
    } = nextProps;
    const {
      programsList: prevProgramsList,
      currentProgramTimestamp: prevCurrentProgramTimestamp,
      playingProgramTimestamp: prevPlayingProgramTimestamp,
      currentChannelId: prevChannelId,
      visible: prevVisible,
    } = this.props;

    let newLoadingState = this.state.loading;
    if (nextChannelId && nextChannelId !== prevChannelId) {
      this.isProgramExtended = false;
      newLoadingState = true;
      this.scrollToIndex = -1;
    }

    const { programs } = this.state;
    if (
      nextPlayingProgramTimestamp &&
      nextPlayingProgramTimestamp !== prevPlayingProgramTimestamp
    ) {
      this.scrollToIndex = this.findActiveProgramIndex(programs, nextPlayingProgramTimestamp);
      if (this.listRef) {
        this.listRef.forceUpdateGrid();
      }
    } else if (
      nextCurrentProgramTimestamp &&
      nextCurrentProgramTimestamp !== prevCurrentProgramTimestamp &&
      !nextPlayingProgramTimestamp
    ) {
      this.scrollToIndex = this.findActiveProgramIndex(programs, nextCurrentProgramTimestamp);
    }
    if (nextProgramsList && nextProgramsList !== prevProgramsList && isExtendedProgramList) {
      this.cache.clearAll();
      this.processPrograms(nextProgramsList, programsMap, nextProps);
    }

    if (
      ((!nextProgramsList || nextProgramsList.length < 3) &&
        this.state.loading &&
        nextVisible &&
        isExtendedProgramList) ||
      (isExtendedProgramList && nextCurrentProgramTimestamp === null) // if program not found
    ) {
      newLoadingState = false;
    }

    if (nextProgramsList && nextVisible && !this.isProgramExtended) {
      loadPrograms();
      this.isProgramExtended = true;
    }

    if (nextVisible !== prevVisible) {
      if (nextVisible) {
        if (this.listRef) {
          if (this.scrollToIndex !== null) {
            this.scrollToRowSet = true;
            this.listRef.scrollToRow(this.scrollToIndex);
          }
        } else {
          this.needExecScrollToRow = true;
        }
      } else if (this.targetScrollReseted) {
        newLoadingState = true;
      }
    }

    if (newLoadingState !== this.state.loading) {
      this.setState({
        loading: newLoadingState,
      });
    }

    if (nextVisible && newLoadingState) {
      if (this.loadingTimeoutId) {
        window.clearTimeout(this.loadingTimeoutId);
      }
      this.loadingTimeoutId = window.setTimeout(this.onLoadingTimeout, 3000);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(_.sortBy(prevState.days), _.sortBy(this.state.days))) {
      this.fillKeyElementsMap();
      this.onScroll(this.scrollTarget);
    }

    if (this.needExecScrollToRow && !this.state.loading && this.listRef) {
      this.scrollToRowSet = true;
      this.listRef.scrollToRow(this.scrollToIndex);
      this.needExecScrollToRow = false;
    }

    if (
      this.props.isExtendedProgramList &&
      this.props.isExtendedProgramList !== prevProps.isExtendedProgramList &&
      this.state.loading &&
      !this.targetScrollReseted &&
      this.state.scrollbarScrollTo.value !== -1
    ) {
      this.setState({ loading: false }); // eslint-disable-line
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.throttledOnResize);
  }

  onResize = () => {
    this.setState({ loading: true });
    this.resizeTimeoutId = setTimeout(this.onResizeTimeout, 500);

    if (
      (window.innerWidth <= 1120 && !this.smallScreen) ||
      (window.innerWidth > 1120 && this.smallScreen)
    ) {
      this.smallScreen = window.innerWidth <= 1120;
      if (this.firstDateCapInstance) {
        this.firstDateCapInstance.setIsHidden(!this.smallScreen);
      }

      if (this.smallScreen) {
        this.topOffset = 0;
        this.dayTopOffset = 0;
        this.lastProgramOffset = 0;
      } else {
        this.topOffset = 30;
        this.dayTopOffset = 85;
        this.lastProgramOffset = 15;
      }
    }
  };

  onResizeTimeout = () => {
    this.resizeTimeoutId = null;
    this.cache.clearAll(); // Clear cache measure after resize window
    this.setState({
      loading: false,
    });
    if (this.listRef && this.scrollToIndex !== -1) {
      this.scrollToRowSet = true;
      this.listRef.scrollToRow(this.scrollToIndex);
    }
  };

  onRowsRendered = () => {
    const dayCaps = this.root.current.querySelectorAll('[data-name="headerCap"]');
    const programElem = this.root.current.querySelector('[data-name="program"]');
    const days = [];

    if (programElem) {
      if (dayCaps.length === 0) {
        days.push({
          day: programElem.getAttribute('data-day'),
          month: programElem.getAttribute('data-month'),
        });
      } else if (dayCaps.length > 0) {
        const capDay = dayCaps[0].getAttribute('data-day'); // get value of top day header elem
        const programDay = programElem.getAttribute('data-day'); // get value of the most top program elem
        if (capDay !== programDay) {
          // this says that there is not day's header, that corresponds to the top program elem
          days.push({
            day: programDay,
            month: programElem.getAttribute('data-month'),
          });
        }
        for (let i = 0, l = dayCaps.length; i < l; ++i) {
          days.push({
            day: dayCaps[i].getAttribute('data-day'),
            month: dayCaps[i].getAttribute('data-month'),
          });
        }
      }

      const { translate } = this.props;
      const newDays = [];
      const nowDate = new Date().getDate().toString();
      const todayText = translate('today', 'сегодня');
      let monthText;
      for (let i = 0, l = days.length; i < l; ++i) {
        if (days[i].day === nowDate) {
          monthText = `${translate(MONTHS_KEYS[days[i].month])}, ${todayText}`;
        } else {
          monthText = translate(MONTHS_KEYS[days[i].month]);
        }
        newDays.push(
          <DateComponent
            day={days[i].day}
            month={monthText}
            key={days[i].day}
            offsetTop={this.dateTranslateYMap[days[i].day] || this.topOffset}
          />
        );
      }
      this.fillKeyElementsMap();
      this.onScroll(this.scrollTarget);
      this.setState({ days: newDays });
    }
  };

  onScroll = (target) => {
    const { scrollTop, scrollLeft } = target;

    if (this.listRef) {
      const { Grid: grid } = this.listRef;
      if (scrollTop !== grid.state.scrollTop) {
        grid.handleScrollEvent({ scrollTop, scrollLeft });
      }
    }

    this.scrollTarget = !_.isEmpty(target)
      ? target
      : document.getElementById('innerScrollContainer');

    const { scrollbarScrollTo, loading } = this.state;
    const { isExtendedProgramList } = this.props;
    if (Math.abs(scrollTop - scrollbarScrollTo.value) > 1) {
      this.targetScrollReseted = true;
    } else {
      this.targetScrollReseted = false;
      if (loading && isExtendedProgramList && scrollbarScrollTo.value >= 0) {
        this.setState({ loading: false });
      }
    }

    if (this.root.current) {
      const headers = this.root.current.querySelectorAll('[data-name="date"]');

      let header;
      let newTop;
      let day;
      let dayElements;
      let top;
      let topWithOffsets;
      let bottom;

      for (let i = 0, l = headers.length; i < l; ++i) {
        header = headers[i];
        day = header.getAttribute('data-day');

        if (day in this.positionMap) {
          dayElements = this.positionMap[day];
          top = _.get(dayElements, 'top.offsetTop', 0) - scrollTop;
          topWithOffsets = top + this.dayTopOffset - this.topOffset;
          bottom = dayElements.bottom ? dayElements.bottom.offsetTop - scrollTop : null;

          if (topWithOffsets > 0) {
            newTop =
              _.get(dayElements, 'top.offsetTop') !== 0 ? top + this.dayTopOffset : this.topOffset;
          } else if (topWithOffsets <= 0 && (bottom >= 0 || bottom === null)) {
            newTop = this.topOffset;
            if (
              bottom !== null &&
              bottom + dayElements.bottom.offsetHeight < newTop + header.offsetHeight
            ) {
              newTop = bottom + _.get(dayElements, 'bottom.offsetHeight', 0) - header.offsetHeight;
            }
          } else {
            newTop = bottom + _.get(dayElements, 'bottom.offsetHeight', 0) - header.offsetHeight;
          }
        } else if (headers.length === 1) {
          newTop = this.topOffset;
        }
        this.dateTranslateYMap[day] = newTop;
        if (newTop !== undefined) {
          header.style.transform = `translateY(${newTop}px)`;
        }
      }
    }
  };

  onListScroll = ({ scrollTop }) => {
    if (this.scrollToRowSet) {
      this.scrollToRowSet = false;
      this.setState({
        scrollbarScrollTo: { value: scrollTop },
      });
    }
  };

  onProgramInstance = (index, instance) => {
    if (instance) {
      this.programComponentList[index] = instance;
    } else {
      delete this.programComponentList[index];
    }
  };

  onFirstDateCapInstance = (instance) => {
    this.firstDateCapInstance = instance;
    if (instance) {
      instance.setIsHidden(!this.smallScreen);
    }
  };

  onLoadingTimeout = () => {
    const { isExtendedProgramList } = this.props;
    const { loading } = this.state;
    this.loadingTimeoutId = null;
    if (isExtendedProgramList && loading) {
      this.setState({
        loading: false,
      });
      this.targetScrollReseted = false;
    }
  };

  fillKeyElementsMap = () => {
    const dateCaps = Array.from(this.root.current.querySelectorAll('[data-name="headerCap"]'));
    let day;

    this.positionMap = {};

    if (dateCaps.length > 0) {
      if (dateCaps[0].previousElementSibling) {
        day = dateCaps[0].previousElementSibling.getAttribute('data-day');
        this.positionMap[day] = { bottom: dateCaps[0].previousElementSibling };
      }

      for (let i = 0, l = dateCaps.length; i < l; ++i) {
        if (i !== 0 && dateCaps[i].previousElementSibling) {
          day = dateCaps[i].previousElementSibling.getAttribute('data-day');
          if (!this.positionMap[day]) {
            this.positionMap[day] = {};
          }
          this.positionMap[day].bottom = dateCaps[i].previousElementSibling;
        }
        day = dateCaps[i].getAttribute('data-day');
        if (!this.positionMap[day]) {
          this.positionMap[day] = {};
        }
        this.positionMap[day] = {
          top: dateCaps[i],
        };
      }
    }
  };

  rowRenderer = ({ key, index, style, parent }) => {
    const { switchProgram, playingProgramTimestamp, toggleExpand, isInteractive } = this.props;
    const { programs } = this.state;
    const singleProgram = programs[index];

    let rendered;
    if (singleProgram) {
      if (singleProgram.isDay) {
        const day = new Date(singleProgram.timestamp * 1000);
        rendered = (
          <CellMeasurer
            key={key}
            cache={this.cache}
            parent={parent}
            columnIndex={0}
            rowIndex={index}
          >
            <DateHeaderCap
              isHidden={!this.smallScreen && index === 0}
              day={day.getDate()}
              month={day.getMonth()}
              style={style}
              onInstance={index === 0 ? this.onFirstDateCapInstance : undefined}
            />
          </CellMeasurer>
        );
      } else if (singleProgram.dummyProgram) {
        rendered = (
          <CellMeasurer
            key={key}
            cache={this.cache}
            parent={parent}
            columnIndex={0}
            rowIndex={index}
          >
            <div style={style} />
          </CellMeasurer>
        );
      } else {
        const time = this.parseProgramDate(singleProgram.startDate);
        const available =
          (singleProgram.channel.type !== 'regularChannel' &&
            singleProgram.state !== 2 &&
            Boolean(singleProgram.virtualObjectId)) ||
          singleProgram.objectId > 0;
        rendered = (
          <CellMeasurer
            key={key}
            cache={this.cache}
            parent={parent}
            columnIndex={0}
            rowIndex={index}
          >
            <Program
              index={index}
              isInteractive={isInteractive || Boolean(playingProgramTimestamp)}
              id={singleProgram.startTimestamp}
              toggleExpand={toggleExpand}
              available={available}
              current={singleProgram.state === 1}
              ended={singleProgram.state === 0}
              playing={singleProgram.startTimestamp === playingProgramTimestamp}
              time={time}
              title={singleProgram.title}
              switchProgram={switchProgram}
              singleProgram={singleProgram}
              onInstance={this.onProgramInstance}
              style={style}
            />
          </CellMeasurer>
        );
      }
    }
    return rendered;
  };

  parseProgramDate = (date) => {
    return `${addZero(date.getHours())}:${addZero(date.getMinutes())}`;
  };

  processPrograms = (programsList, programsMap, props) => {
    const programs = [];
    let lastDate = null;
    let date;
    let dateObject;
    let program;
    for (let i = 0, l = programsList.length; i < l; ++i) {
      program = programsMap[programsList[i]];
      dateObject = new Date(programsList[i] * 1000);
      date = dateObject.getDate();
      if (date !== lastDate) {
        lastDate = date;

        // *********************************************************
        // very bad kocTblL'
        // if api sent only one program from previous date
        // so in fact we have just 1 program in 1 day
        // – это грустро или весело?
        // – это печально :(
        if (programs.length === 2 && programs[0].isDay) {
          programs.length = 0;
        }
        // *********************************************************

        programs.push({ isDay: true, key: date, timestamp: program.startTimestamp });
      }
      programs.push(program);
      if (program.state === 1 && this.scrollToIndex === -1 && programs.length > 0) {
        // 0 - past, 1 - current, 2 - further
        this.scrollToIndex = programs.length - 1;
        this.needExecScrollToRow = true;
      }
    }
    const lastDayObject = programs.find((obj) => obj.key === lastDate && obj.isDay);
    const lastDayPrograms = programs.length - programs.indexOf(lastDayObject) - 1;
    if (lastDayPrograms < 3) {
      for (let i = 0; i < 3 - lastDayPrograms; i++) {
        programs.push({ dummyProgram: true });
      }
    }

    // try to find playing program index;
    const { playingProgramTimestamp } = props;
    const newIndex = this.findActiveProgramIndex(programs, playingProgramTimestamp);
    if (newIndex !== -1) {
      this.scrollToIndex = newIndex;
    }

    this.setState({ programs, days: [] }, () => this.onRowsRendered());
  };

  findActiveProgramIndex = (programsList, activeTimestamp) => {
    let newIndex = -1;
    if (programsList && programsList.length > 0 && activeTimestamp) {
      newIndex = _.findIndex(programsList, { startTimestamp: activeTimestamp });
    }
    return newIndex;
  };

  assignListRef = (ref) => {
    this.listRef = ref;
    if (this.listRef && this.props.visible && !this.state.loading) {
      if (this.scrollToIndex !== null) {
        this.scrollToRowSet = true;
        this.listRef.scrollToRow(this.scrollToIndex);
      }
    } else {
      this.needExecScrollToRow = true;
    }
  };

  render() {
    const { visible, programsList, translate } = this.props;
    const { programs, days, loading, scrollbarScrollTo } = this.state;
    return (
      <DailyProgramStyled ref={this.root}>
        {loading && (
          <AbsoluteWrapperStyled>
            <Loader type="video" />
          </AbsoluteWrapperStyled>
        )}
        <DailyContent visible={visible && !loading}>
          {programsList.length < 3 && (
            <AbsoluteWrapperStyled>
              <NoPrograms>
                <NoProgramsImage src="//s6.vcdn.biz/static/70666071/no_programs.png" alt="" />
                <StyledText color="gray_darksmoke">
                  {translate('noProgram', 'Программа для данного телеканала отсутствует')}
                </StyledText>
              </NoPrograms>
            </AbsoluteWrapperStyled>
          )}
          <React.Fragment>
            <DaysContainer>{days}</DaysContainer>
            <StyledScrollbar
              heightRelativeToParent="100%"
              onScroll={this.onScroll}
              scrollTo={scrollbarScrollTo}
            >
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    ref={this.assignListRef}
                    height={height}
                    width={width}
                    deferredMeasurementCache={this.cache}
                    style={listStyle}
                    rowHeight={this.cache.rowHeight}
                    rowRenderer={this.rowRenderer}
                    rowCount={programs.length}
                    onRowsRendered={this.onRowsRendered}
                    scrollToAlignment="center"
                    onScroll={this.onListScroll}
                  />
                )}
              </AutoSizer>
            </StyledScrollbar>
          </React.Fragment>
        </DailyContent>
      </DailyProgramStyled>
    );
  }
}

DailyProgram.propTypes = {
  visible: PropTypes.bool,
  programsList: PropTypes.array,
  programsMap: PropTypes.object,
  switchProgram: PropTypes.func,
  currentProgramTimestamp: PropTypes.number,
  playingProgramTimestamp: PropTypes.number,
  currentChannelId: PropTypes.number,
  loadPrograms: PropTypes.func,
  translate: PropTypes.func,
  toggleExpand: PropTypes.func,
  isExtendedProgramList: PropTypes.bool,
  isInteractive: PropTypes.bool,
};

DailyProgram.defaultProps = {
  visible: false,
  switchProgram: Function.prototype,
  currentProgramTimestamp: null,
  playingProgramTimestamp: null,
  currentChannelId: null,
  loadPrograms: Function.prototype,
  translate: Function.prototype,
  programsList: [],
  programsMap: {},
  toggleExpand: Function.prototype,
  isExtendedProgramList: false,
  isInteractive: false,
};

export default DailyProgram;
